/**
 * NekoHR-local task estimate overlays (browser localStorage).
 * Overrides GitLab / Asana API estimates for Koneko B scoring and display;
 * use “Push estimate” on Koneko B to persist estimates to the backing service.
 */

const STORAGE_KEY = "konekoB:estimateOverlays";

/**
 * @typedef {{ estimateMinutes: number, updatedAt: string }} EstimateOverlayRow
 * @typedef {Record<string, EstimateOverlayRow>} EstimateOverlayMap
 */

export function loadEstimateOverlays() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return parsed;
  } catch {
    return {};
  }
}

/** @param {EstimateOverlayMap} map */
export function saveEstimateOverlays(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota */
  }
}

/** @param {import("./canonicalTaskFeed").CanonicalTask} task @param {EstimateOverlayMap} overlays */
export function mergeTaskWithEstimateOverlay(task, overlays) {
  const id = task.sourceTaskId;
  const row = overlays[id];
  if (
    !row ||
    !Number.isFinite(Number(row.estimateMinutes)) ||
    Number(row.estimateMinutes) < 0
  ) {
    return {
      ...task,
      nekohrEstimateOverride: false,
      sourceEstimateMinutes: task.estimateMinutes,
    };
  }

  const minutes = Math.round(Number(row.estimateMinutes));

  return {
    ...task,
    sourceEstimateMinutes: task.estimateMinutes,
    estimateMinutes: minutes,
    nekohrEstimateOverride: true,
    nekohrEstimateUpdatedAt: row.updatedAt || "",
  };
}

/**
 * @param {EstimateOverlayMap} map
 * @param {string} sourceTaskId
 * @param {number|null|undefined} hours — null/undefined to remove override
 */
export function setEstimateOverlayHours(map, sourceTaskId, hours) {
  const next = {...map};
  const id = String(sourceTaskId || "").trim();
  if (!id) return next;

  if (hours === null || hours === undefined || hours === "") {
    delete next[id];
    return next;
  }

  const h = Number(hours);
  if (!Number.isFinite(h) || h < 0) {
    delete next[id];
    return next;
  }

  const estimateMinutes = Math.round(h * 60);
  next[id] = {
    estimateMinutes,
    updatedAt: new Date().toISOString(),
  };
  return next;
}
